import type { ThemePalette } from '@kancelaria/shared';
import { useEffect, type ReactNode } from 'react';
import { create } from 'zustand';
import { syncThemeColor } from '../store/ui.js';

const VAR: Record<keyof ThemePalette, string> = {
  bg: '--bg',
  panel: '--panel',
  panel2: '--panel-2',
  line: '--line',
  text: '--text',
  muted: '--muted',
};

function textOn(hex: string): string {
  const v = hex.replace('#', '');
  if (v.length !== 6) return '#fff';
  const channel = (i: number): number => {
    const c = Number.parseInt(v.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const light = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return light > 0.3 ? '#1d1d1f' : '#fff';
}

function rgbOf(hex: string): string | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = Number.parseInt(m[1]!, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

function block(palette: ThemePalette | undefined, accent: string): string {
  const parts: string[] = [];
  if (palette) {
    for (const key of Object.keys(VAR) as (keyof ThemePalette)[]) {
      if (palette[key]) parts.push(`${VAR[key]}:${palette[key]}`);
    }
    if (palette.bg) {
      const rgb = rgbOf(palette.bg);
      if (rgb) parts.push(`--glass-rgb:${rgb}`);
    }
    if (palette.panel2) parts.push(`--head:${palette.panel2}`);
    if (palette.line) {
      parts.push(`--glass-edge:${palette.line}`);
      if (palette.panel) parts.push(`--line-soft:color-mix(in srgb, ${palette.line} 70%, ${palette.panel})`);
    }
  }
  if (accent === RAINBOW) {
    parts.push('--accent-text:#fff');
  } else if (accent) {
    parts.push(`--accent:${accent}`);
    parts.push(`--accent-color:${accent}`);
    parts.push(`--accent-text:${textOn(accent)}`);
  }
  return parts.join(';');
}

/** Акцент, що переливається: колір курсу не один, а плавно йде по колу. */
export const RAINBOW = 'rainbow';
const RAINBOW_COLORS = ['#ff3b30', '#ff9500', '#34c759', '#00b8a9', '#af52de', '#ff2d92'];
const RAINBOW_CSS = (() => {
  const steps = [...RAINBOW_COLORS, RAINBOW_COLORS[0]!]
    .map((c, i, all) => `${((i / (all.length - 1)) * 100).toFixed(2)}%{--accent:${c};--accent-color:${c}}`)
    .join('');
  return (
    `@property --accent{syntax:'<color>';inherits:true;initial-value:#ff9000}` +
    `@property --accent-color{syntax:'<color>';inherits:true;initial-value:#ff9000}` +
    `@keyframes accent-rainbow{${steps}}` +
    `:root{animation:accent-rainbow 24s linear infinite}` +
    `@media (prefers-reduced-motion:reduce){:root{animation:none;--accent:${RAINBOW_COLORS[0]};--accent-color:${RAINBOW_COLORS[0]}}}`
  );
})();

/** Ключ у localStorage: index.html ставить із нього кольори до першого кадру. */
const BOOT_KEY = 'kanc.theme-css';

/**
 * Кольори курсу накладаємо змінними поверх типової теми — так світла й темна
 * лишаються окремими, а порожнє поле означає «як у CSS».
 */
type Palettes = { light: ThemePalette; dark: ThemePalette };

/**
 * Незбережений вибір із налаштувань. Таблиця стилів курсу одна — у застосунку;
 * друга поряд не годилась: «переливається» вона вмикала, а от вимкнути не могла,
 * бо анімація старої таблиці перебиває будь-яке нове значення.
 */
const usePreview = create<{ value: { palettes?: Palettes; accent?: string } | null }>(() => ({ value: null }));

/** Показати вибір із налаштувань одразу, до «Зберегти»; зі сторінки пішли — повернути. */
export function ThemePreview({ palettes, accent }: { palettes?: Palettes; accent?: string }): ReactNode {
  useEffect(() => {
    usePreview.setState({ value: { palettes, accent } });
  }, [palettes, accent]);
  useEffect(() => () => usePreview.setState({ value: null }), []);
  return null;
}

export function ThemeStyles(props: { palettes?: Palettes; accent?: string }): ReactNode {
  const preview = usePreview(s => s.value);
  const palettes = preview ? preview.palettes : props.palettes;
  const accent = preview ? preview.accent : props.accent;
  const color = accent?.trim() ?? '';
  const light = block(palettes?.light, color);
  const dark = block(palettes?.dark, color);
  // Кольори курсу приходять із налаштуваннями, тобто пізніше за перший
  // застосунок теми — смугу браузера доводиться перепитати після них.
  useEffect(() => {
    const id = requestAnimationFrame(() => syncThemeColor());
    return () => cancelAnimationFrame(id);
  }, [light, dark]);
  const css =
    light || dark
      ? `:root{${light}}:root[data-theme='dark']{${dark}}${color === RAINBOW ? RAINBOW_CSS : ''}`
      : '';
  // Кольори курсу приходять із сесією, а вона — після заставки й входу. Тож
  // запам'ятовуємо їх, і наступний запуск малює заставку вже в тих кольорах.
  useEffect(() => {
    // Своя таблиця вже в документі — стартову знімаємо, інакше вона її перекрила б.
    document.getElementById('theme-boot')?.remove();
    try {
      if (preview) return;
      if (css) localStorage.setItem(BOOT_KEY, css);
      else localStorage.removeItem(BOOT_KEY);
    } catch {
      /* сховище закрите — просто без кешу */
    }
  }, [css, preview]);
  return css ? <style>{css}</style> : null;
}
